import { Component } from '@theme/component';

/**
 * MELUVI — Dispatch Promise.
 *
 * Computes the wording (and, before the cut-off hour on a dispatch day, a live HH:MM:SS countdown)
 * from the real current time in the configured time zone. It only states the shop's configured
 * dispatch policy; it never changes real fulfilment dates.
 *
 * Calendar math runs on plain year-month-day values (UTC date arithmetic) and the remaining time
 * is the difference of Warsaw wall-clock times, so DST changes and the visitor's own time zone
 * cannot shift the result. The remaining time is recomputed from the current time on every tick,
 * never counted down from page load, so it stays correct after the tab was in the background.
 */

const DEFAULT_CUTOFF_MINUTES = 16 * 60;
const COUNTDOWN_TICK_MS = 1000;
const IDLE_TICK_MS = 10000;
const LOOKAHEAD_DAYS = 14;
const DAY_PHRASES = ['w niedzielę', 'w poniedziałek', 'we wtorek', 'w środę', 'w czwartek', 'w piątek', 'w sobotę'];
const COUNTDOWN_TOKEN = '{odliczanie}';

/**
 * @param {string | undefined} value
 * @returns {{minutes: number, label: string}}
 */
function parseCutoff(value) {
  const match = /^\s*(\d{1,2}):(\d{2})\s*$/.exec(value ?? '');
  const hours = match ? Number(match[1]) : NaN;
  const minutes = match ? Number(match[2]) : NaN;

  if (!match || hours > 23 || minutes > 59) {
    return { minutes: DEFAULT_CUTOFF_MINUTES, label: '16:00' };
  }

  return { minutes: hours * 60 + minutes, label: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}` };
}

/**
 * Current calendar date and clock time in a time zone. Throws RangeError for an unknown zone.
 * @param {Date} now
 * @param {string} timeZone
 */
function getZonedNow(now, timeZone) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);

  /** @param {string} type */
  const get = (type) => Number(parts.find((part) => part.type === type)?.value);

  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    seconds: get('hour') * 3600 + get('minute') * 60 + get('second'),
  };
}

/** @param {Date} date UTC-based date */
function toIsoDate(date) {
  return date.toISOString().slice(0, 10);
}

/** @param {number} total seconds @returns {string} HH:MM:SS */
function formatCountdown(total) {
  const pad = (/** @type {number} */ value) => String(value).padStart(2, '0');
  return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
}

/**
 * Splits a template into text and countdown segments, filling {godzina} and {dzien}.
 * @param {string} template
 * @param {{godzina?: string, dzien?: string}} values
 * @param {boolean} withCountdown keep a countdown segment (otherwise the token is removed)
 * @returns {Array<string | {countdown: true}>}
 */
function buildSegments(template, values, withCountdown) {
  const filled = template.replace(/\{(godzina|dzien)\}/g, (_, key) => values[/** @type {'godzina' | 'dzien'} */ (key)] ?? '');
  const pieces = filled.split(COUNTDOWN_TOKEN);
  /** @type {Array<string | {countdown: true}>} */
  const segments = [];

  pieces.forEach((piece, index) => {
    if (piece) segments.push(piece);
    if (index < pieces.length - 1 && withCountdown) segments.push({ countdown: true });
  });

  return segments;
}

export class MeluviDispatchPromise extends Component {
  /** @type {number | undefined} */
  #timer;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {string} */
  #renderedKey = '';

  /** @type {HTMLElement | null} */
  #countdownEl = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController.abort();
    this.#abortController = new AbortController();
    document.addEventListener('visibilitychange', this.#onVisibility, { signal: this.#abortController.signal });

    this.#renderedKey = '';
    this.#update();
  }

  updatedCallback() {
    super.updatedCallback();

    // The morph may have rewritten the headline; force a fresh render.
    this.#renderedKey = '';
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.clearTimeout(this.#timer);
    this.#timer = undefined;
    this.#abortController.abort();
  }

  #onVisibility = () => {
    if (!document.hidden) this.#update();
  };

  /** Single self-rescheduling timer: re-running always replaces the pending one. */
  #update() {
    window.clearTimeout(this.#timer);

    const model = this.#compute();
    this.#render(model);

    const delay = model?.countdown != null ? COUNTDOWN_TICK_MS - (Date.now() % COUNTDOWN_TICK_MS) + 5 : IDLE_TICK_MS;
    this.#timer = window.setTimeout(() => this.#update(), delay);
  }

  /**
   * @param {{segments: Array<string | {countdown: true}>, countdown: number | null} | null} model
   */
  #render(model) {
    const headline = this.querySelector('[data-dp-headline]');
    // null = cannot be determined reliably: keep the safe server-rendered text.
    if (!headline || !model) return;

    const digits = model.countdown != null ? formatCountdown(model.countdown) : '';
    const key = model.segments.map((segment) => (typeof segment === 'string' ? segment : '\u0000')).join('|');

    if (key === this.#renderedKey) {
      if (this.#countdownEl && this.#countdownEl.textContent !== digits) this.#countdownEl.textContent = digits;
      return;
    }

    /** @type {HTMLElement | null} */
    let countdownEl = null;
    const nodes = model.segments.map((segment) => {
      if (typeof segment === 'string') return document.createTextNode(segment);

      countdownEl = document.createElement('span');
      countdownEl.className = 'meluvi-dp__countdown';
      countdownEl.textContent = digits;
      return countdownEl;
    });

    headline.replaceChildren(...nodes);
    this.#countdownEl = countdownEl;
    this.#renderedKey = key;
    this.toggleAttribute('data-countdown', countdownEl !== null);
  }

  /**
   * @returns {{segments: Array<string | {countdown: true}>, countdown: number | null} | null}
   */
  #compute() {
    const { dataset } = this;
    const cutoff = parseCutoff(dataset.cutoff);
    const cutoffSeconds = cutoff.minutes * 60;
    const days = new Set(
      (dataset.days ?? '')
        .split(',')
        .filter((day) => day !== '')
        .map(Number)
    );
    const holidays = new Set((dataset.holidays ?? '').match(/\d{4}-\d{2}-\d{2}/g) ?? []);
    const showCountdown = dataset.showCountdown === 'true';

    if (days.size === 0) return null;

    let zoned;
    try {
      zoned = getZonedNow(new Date(), dataset.timezone || 'Europe/Warsaw');
    } catch {
      return null;
    }

    if ([zoned.year, zoned.month, zoned.day, zoned.seconds].some(Number.isNaN)) return null;

    /** @param {Date} date */
    const isDispatchDay = (date) => days.has(date.getUTCDay()) && !holidays.has(toIsoDate(date));

    const today = new Date(Date.UTC(zoned.year, zoned.month - 1, zoned.day));
    const todayWorks = isDispatchDay(today);

    if (todayWorks && zoned.seconds < cutoffSeconds) {
      const remaining = cutoffSeconds - zoned.seconds;
      const template = showCountdown ? dataset.textBefore : dataset.textBeforeStatic;
      const segments = buildSegments(template ?? '', { godzina: cutoff.label }, showCountdown);

      return { segments, countdown: segments.some((segment) => typeof segment !== 'string') ? remaining : null };
    }

    let next = null;
    for (let offset = 1; offset <= LOOKAHEAD_DAYS; offset += 1) {
      const candidate = new Date(today.getTime() + offset * 86400000);
      if (isDispatchDay(candidate)) {
        next = { date: candidate, offset };
        break;
      }
    }

    if (!next) return null;

    // "jutro" only after the cut-off on a dispatch day; weekends and days off always name the day.
    const dayPhrase = todayWorks && next.offset === 1 ? 'jutro' : (DAY_PHRASES[next.date.getUTCDay()] ?? '');
    const template = todayWorks ? dataset.textAfter : dataset.textOff;

    return { segments: buildSegments(template ?? '', { godzina: cutoff.label, dzien: dayPhrase }, false), countdown: null };
  }
}

if (!customElements.get('meluvi-dispatch-promise')) {
  customElements.define('meluvi-dispatch-promise', MeluviDispatchPromise);
}
